/**
 * ASCII Art Generator - Braille 渲染引擎
 *
 * 使用Unicode盲文字符(U+2800-U+28FF)实现高分辨率ASCII艺术
 * 每个Braille字符代表2x4像素点阵
 * 支持动画渲染
 * Wave 8.3: 支持灰度预计算缓存
 */

import type {
  IRenderEngine,
  FullRenderSettings,
  RenderResult,
  StyleType,
  RGB,
} from '../types'
import type { AnimationContext } from '../animation/types'
import type { RenderContextExt } from './animationHelpers'
import type { GrayscaleCacheData } from '../algorithms/grayscale'
import {
  applyContrastAndBrightness,
  applyColorMode,
  getPixelGrayscaleFast,
  getPixelRGBFast,
  rgbToGrayscale,
} from '../algorithms/grayscale'
import {
  calculateCharacterAnimation,
  createStaticContext,
  hasGrayscaleCache,
} from './animationHelpers'

/**
 * Braille 渲染引擎
 * Wave 8.3: 支持灰度预计算缓存优化
 */
export class BrailleEngine implements IRenderEngine {
  readonly name: StyleType = 'braille'

  /**
   * 渲染图片为Braille艺术（支持动画）
   * Wave 8.3: 支持灰度预计算缓存优化
   */
  render(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: FullRenderSettings,
    canvas: HTMLCanvasElement,
    context?: AnimationContext
  ): RenderResult {
    // 使用提供的动画上下文，或创建静态上下文
    const animContext: RenderContextExt = context ?? createStaticContext()

    const imageData = this.getImageData(source)
    const { width: imgWidth, height: imgHeight } = imageData

    const { fontSize, contrast, brightness, invert, charSpacing } = settings

    // Braille字符宽度约为高度的0.5倍
    const baseCharWidth = fontSize * 0.5
    const charWidth = baseCharWidth * charSpacing
    const charHeight = fontSize

    // 根据目标字符大小计算输出字符数量（考虑间距）
    const cols = Math.max(1, Math.floor(imgWidth / baseCharWidth / charSpacing))
    const rows = Math.max(1, Math.floor(imgHeight / fontSize))

    canvas.width = cols * charWidth
    canvas.height = rows * charHeight

    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = settings.backgroundColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    ctx.font = `${fontSize}px monospace`
    ctx.textBaseline = 'top'

    // 计算采样区域大小
    const sampleWidth = imgWidth / cols
    const sampleHeight = imgHeight / rows

    // Wave 8.3: 检查是否有灰度缓存
    const useCache = hasGrayscaleCache(animContext)
    const grayscaleCache = animContext.grayscaleCache

    // 计算全局阈值
    let totalBrightness = 0
    // Wave 8.3: 使用缓存或原始计算
    if (useCache && grayscaleCache) {
      const { grayscale, width: cacheWidth, height: cacheHeight } = grayscaleCache
      for (let y = 0; y < cacheHeight; y++) {
        for (let x = 0; x < cacheWidth; x++) {
          totalBrightness += grayscale[y * cacheWidth + x]
        }
      }
    } else {
      for (let y = 0; y < imgHeight; y++) {
        for (let x = 0; x < imgWidth; x++) {
          const idx = (y * imgWidth + x) * 4
          const gray = rgbToGrayscale(imageData.data[idx], imageData.data[idx + 1], imageData.data[idx + 2])
          totalBrightness += gray
        }
      }
    }
    const globalThreshold = totalBrightness / (imgWidth * imgHeight)

    // 检查动画或鼠标交互是否启用
    const animationEnabled = settings.ditherAnimationEnabled || settings.dither2AnimationEnabled
    const mouseInteractionEnabled = settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled
    const needsAnimation = animationEnabled || mouseInteractionEnabled

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        // 计算采样区域
        const sampleX = col * sampleWidth
        const sampleY = row * sampleHeight

        // Wave 8.3: 使用缓存或原始采样
        const { char, color } = useCache && grayscaleCache
          ? this.getBrailleFromBlockFast(grayscaleCache, sampleX, sampleY, sampleWidth, sampleHeight, globalThreshold, contrast, brightness, invert)
          : this.getBrailleFromBlock(imageData, sampleX, sampleY, sampleWidth, sampleHeight, globalThreshold, contrast, brightness, invert)

        // 计算字符基础位置
        const baseX = col * charWidth
        const baseY = row * charHeight

        // 应用动画或鼠标交互效果
        let drawX = baseX
        let drawY = baseY

        if (needsAnimation) {
          const animResult = calculateCharacterAnimation(
            baseX, baseY, animContext, settings, charWidth, charHeight
          )

          drawX += animResult.offsetX
          drawY += animResult.offsetY
        }

        // 应用颜色模式
        const finalColor = applyColorMode(color, settings.colorMode, settings.customColor)
        ctx.fillStyle = this.applyOpacity(finalColor, settings.opacity)
        ctx.fillText(char, drawX, drawY)
      }
    }

    return {
      canvas,
      width: canvas.width,
      height: canvas.height,
      charWidth,
      charHeight,
    }
  }

  private getBrailleFromBlock(
    imageData: ImageData,
    sampleX: number,
    sampleY: number,
    sampleWidth: number,
    sampleHeight: number,
    threshold: number,
    contrast: number,
    brightness: number,
    invert: boolean
  ): { char: string; color: RGB; brightness: number } {
    const { width: imgWidth, height: imgHeight, data } = imageData
    let bits = 0
    let r = 0, g = 0, b = 0, count = 0
    let totalGray = 0

    // Braille点位置 - 在采样区域内均匀分布8个采样点
    const dotPositions = [
      [0.25, 0.125], [0.25, 0.375], [0.25, 0.625], [0.25, 0.875],
      [0.75, 0.125], [0.75, 0.375], [0.75, 0.625], [0.75, 0.875],
    ]

    const bitMapping = [0x01, 0x02, 0x04, 0x40, 0x08, 0x10, 0x20, 0x80]

    for (let i = 0; i < 8; i++) {
      const [rx, ry] = dotPositions[i]
      const px = Math.floor(sampleX + rx * sampleWidth)
      const py = Math.floor(sampleY + ry * sampleHeight)

      if (px >= 0 && py >= 0 && px < imgWidth && py < imgHeight) {
        const idx = (py * imgWidth + px) * 4
        const gray = Math.round(0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2])
        const adjusted = applyContrastAndBrightness(gray, contrast, brightness * 255)
        const finalGray = invert ? 255 - adjusted : adjusted

        r += data[idx]
        g += data[idx + 1]
        b += data[idx + 2]
        count++
        totalGray += finalGray

        if (finalGray > threshold) {
          bits |= bitMapping[i]
        }
      }
    }

    return {
      char: String.fromCharCode(0x2800 + bits),
      color: count > 0 ? { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) } : { r: 128, g: 128, b: 128 },
      brightness: count > 0 ? Math.round(totalGray / count) : 128,
    }
  }

  /**
   * Wave 8.3: 从灰度缓存快速获取Braille字符
   * 直接从预计算的灰度数组读取，避免重复计算
   */
  private getBrailleFromBlockFast(
    cache: GrayscaleCacheData,
    sampleX: number,
    sampleY: number,
    sampleWidth: number,
    sampleHeight: number,
    threshold: number,
    _contrast: number,  // 缓存中已包含对比度调整
    _brightness: number,  // 缓存中已包含亮度调整
    invert: boolean
  ): { char: string; color: RGB; brightness: number } {
    const { width: imgWidth, height: imgHeight } = cache
    let bits = 0
    let r = 0, g = 0, b = 0, count = 0
    let totalGray = 0

    // Braille点位置 - 在采样区域内均匀分布8个采样点
    const dotPositions = [
      [0.25, 0.125], [0.25, 0.375], [0.25, 0.625], [0.25, 0.875],
      [0.75, 0.125], [0.75, 0.375], [0.75, 0.625], [0.75, 0.875],
    ]

    const bitMapping = [0x01, 0x02, 0x04, 0x40, 0x08, 0x10, 0x20, 0x80]

    for (let i = 0; i < 8; i++) {
      const [rx, ry] = dotPositions[i]
      const px = Math.floor(sampleX + rx * sampleWidth)
      const py = Math.floor(sampleY + ry * sampleHeight)

      if (px >= 0 && py >= 0 && px < imgWidth && py < imgHeight) {
        // Wave 8.3: 直接从缓存读取灰度值（已包含对比度亮度调整）
        const gray = getPixelGrayscaleFast(cache, px, py)
        const finalGray = invert ? 255 - gray : gray

        // 读取RGB值
        const rgb = getPixelRGBFast(cache, px, py)
        r += rgb.r
        g += rgb.g
        b += rgb.b
        count++
        totalGray += finalGray

        if (finalGray > threshold) {
          bits |= bitMapping[i]
        }
      }
    }

    return {
      char: String.fromCharCode(0x2800 + bits),
      color: count > 0 ? { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) } : { r: 128, g: 128, b: 128 },
      brightness: count > 0 ? Math.round(totalGray / count) : 128,
    }
  }

  getOutputSize(
    imageWidth: number,
    imageHeight: number,
    settings: FullRenderSettings
  ): { width: number; height: number } {
    const { fontSize, charSpacing } = settings
    const baseCharWidth = fontSize * 0.5
    const charWidth = baseCharWidth * charSpacing
    const charHeight = fontSize
    const cols = Math.max(1, Math.floor(imageWidth / baseCharWidth / charSpacing))
    const rows = Math.max(1, Math.floor(imageHeight / fontSize))

    return {
      width: cols * charWidth,
      height: rows * charHeight,
    }
  }

  private getImageData(source: HTMLImageElement | HTMLCanvasElement | ImageData): ImageData {
    if (source instanceof ImageData) return source
    const canvas = document.createElement('canvas')
    canvas.width = source.width
    canvas.height = source.height
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(source, 0, 0)
    return ctx.getImageData(0, 0, canvas.width, canvas.height)
  }

  private applyOpacity(color: RGB, opacity: number): string {
    if (opacity < 1) {
      return `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`
    }
    return `rgb(${color.r}, ${color.g}, ${color.b})`
  }
}

export const brailleEngine = new BrailleEngine()